// Public-surface configuration. The marketing app now mounts real Clerk for account creation — the
// signup page renders Clerk's prebuilt <SignUp>. The patient origin is still supplied via
// env (VITE_PATIENT_ORIGIN) so cross-app URLs (the post-signup redirect + the sign-in link) stay
// portable across environments (WF09 known risk). Falls back to the local patient dev port when unset.
const PATIENT_ORIGIN = (import.meta.env.VITE_PATIENT_ORIGIN as string | undefined) ?? "";

// Real Clerk on the signup page. Mirrors the patient app: when the publishable key is present we
// mount <ClerkProvider> + render Clerk's <SignUp>; when absent the page degrades to the hand-off link
// (so a keyless build still works). Publishable keys are public (browser-shipped, not a secret).
export const CLERK_PUBLISHABLE_KEY =
  (import.meta.env.VITE_CLERK_PUBLISHABLE_KEY as string | undefined) || "";

export const CLERK_ENABLED = CLERK_PUBLISHABLE_KEY.length > 0;

const DEV_PATIENT_FALLBACK = "http://localhost:5174";

/** Absolute base origin of the patient portal (no trailing slash). */
export function patientOrigin(): string {
  const origin = PATIENT_ORIGIN.trim() || DEV_PATIENT_FALLBACK;
  return origin.replace(/\/+$/, "");
}

/**
 * Build the patient-portal signup hand-off URL. Carries only non-PHI contact info (name, email) so
 * the portal can prefill account creation — never any health information (D5).
 */
export function patientSignupUrl(params: { name?: string; email?: string; tier?: string } = {}) {
  const url = new URL("/signup", patientOrigin());
  if (params.name) url.searchParams.set("name", params.name);
  if (params.email) url.searchParams.set("email", params.email);
  if (params.tier) url.searchParams.set("tier", params.tier);
  return url.toString();
}

/** Absolute URL of the patient-portal sign-in screen. */
export function patientSignInUrl() {
  return new URL("/sign-in", patientOrigin()).toString();
}

// The API origin backs the single public call this site makes (the contact/lead form). Marketing
// stays SDK-free — a plain fetch keeps the bundle small and avoids pulling an auth client into a
// session-less surface. Supplied via env (VITE_API_ORIGIN); falls back to the local dev API.
const API_ORIGIN = (import.meta.env.VITE_API_ORIGIN as string | undefined) ?? "";
const DEV_API_FALLBACK = "http://localhost:8787";

/** Absolute base origin of the API (no trailing slash). */
export function apiOrigin(): string {
  const origin = API_ORIGIN.trim() || DEV_API_FALLBACK;
  return origin.replace(/\/+$/, "");
}

/**
 * Submit the public contact/lead form. Carries ONLY non-PHI contact info (name, email) plus a short
 * non-clinical note — never any health information (D5). Resolves on success; throws on failure so
 * the caller can show an inline error.
 */
export async function submitContact(input: { name: string; email: string; message: string }): Promise<void> {
  const res = await fetch(`${apiOrigin()}/v1/public/contact`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(input),
  });
  if (!res.ok) throw new Error(`contact failed (${res.status})`);
}
